// PackageFeeTypeBottomSheet.tsx

import { useToast } from '@contexts/Toast';
import { errorMessage, WeightType } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  FeeType,
  FeeTypeListProps,
  FeeTypeSheetProps,
  ServiceRateFeetypeSelection,
} from '@helpers/Models';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useEffect, useState } from 'react';
import {
  faCheck,
  faXmark,
  faPen,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Assets } from '@helpers/Constants/Assets';

// Extend FeeType locally to include deletedAt (which comes from the API)
// interface FeeTypeWithDeleted extends FeeType {
//   deletedAt?: string | null;
// }

// Extend the props to accept usedFeeTypeIds
interface PackageFeeTypeBottomSheetProps extends FeeTypeSheetProps {
  usedFeeTypeIds?: number[];
}

export const PackageFeeTypeBottomSheet: React.FC<PackageFeeTypeBottomSheetProps> = ({
  onClose,
  onSelect,
  usedFeeTypeIds = [],
}) => {
  const { showToastMessage } = useToast();
  const [allFeeTypes, setAllFeeTypes] = useState<FeeType[]>([]);
  const [filteredFeeTypes, setFilteredFeeTypes] = useState<FeeType[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<FeeTypeListProps>({
    feeType: '',
    availableFlag: true,
  });
  const [editFormData, setEditFormData] = useState<FeeTypeListProps>({
    feeType: '',
    availableFlag: false,
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedRow, setSelectedRow] = useState<ServiceRateFeetypeSelection>();
  const [searchKey, setSearchKey] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Client-side filtering (display only active fee types)
  useEffect(() => {
    const active = allFeeTypes.filter((fee) => !fee.deletedAt);
    if (!searchKey.trim()) {
      setFilteredFeeTypes(active);
    } else {
      const filtered = active.filter((feeType) =>
        feeType.feeType.toLowerCase().includes(searchKey.toLowerCase())
      );
      setFilteredFeeTypes(filtered);
    }
  }, [searchKey, allFeeTypes]);

  // Fetch all fee types on mount
  useEffect(() => {
    fetchFeeTypes();
  }, []);

  const fetchFeeTypes = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await Axios.post(ApiEndpoints.FETCH_PACKAGE_FEETYPE, {
        searchKey: '',
      });
      const feeTypesData = response.data.data.feeTypes;
      setAllFeeTypes(feeTypesData);
      // filteredFeeTypes will be updated by the effect above
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_FEETYPES, 'error');
    }
    setIsLoading(false);
  };

  const handleRowClick = (feeType: ServiceRateFeetypeSelection): void => {
    setSelectedRow(feeType);
    onSelect(feeType);
    onClose();
  };

  const handleEdit = (index: number): void => {
    setEditingIndex(index);
    setEditFormData({
      feeType: filteredFeeTypes[index].feeType,
      availableFlag: filteredFeeTypes[index].availableFlag,
    });
  };

  const handleAddFeeType = async (): Promise<void> => {
    setErrors({});

    if (!formData.feeType.trim()) {
      setErrors({
        feeType: errorMessage.FIELD_REQUIRED,
      });
      return;
    }

    const trimmedName = formData.feeType.trim();
    const lowerName = trimmedName.toLowerCase();

    // 1. Check if a deleted fee type with the same name exists
    const deletedMatch = allFeeTypes.find(
      (fee) =>
        fee.deletedAt &&
        fee.feeType.trim().toLowerCase() === lowerName
    );

    if (deletedMatch) {
      // Restore it
      try {
        await Axios.post(ApiEndpoints.RESTORE_PACKAGE_FEETYPE, {
          ids: [deletedMatch.id],
        });
        // Refresh the list
        await fetchFeeTypes();
        // Select the restored fee type and close
        const restored: ServiceRateFeetypeSelection = {
          id: deletedMatch.id!,
          feeType: deletedMatch.feeType,
          unit: deletedMatch.unit,
        };
        setSelectedRow(restored);
        onSelect(restored);
        onClose();
        showToastMessage(`Fee type "${deletedMatch.feeType}" restored.`, 'success');
        setFormData({ feeType: '', availableFlag: true });
        setIsAdding(false);
        return;
      } catch (error) {
        showToastMessage('Failed to restore fee type.', 'error');
        return;
      }
    }

    // 2. Check for duplicate among active fee types
    const isDuplicate = allFeeTypes.some(
      (fee) =>
        !fee.deletedAt &&
        fee.feeType.trim().toLowerCase() === lowerName
    );

    if (isDuplicate) {
      setErrors({
        isDuplicate: errorMessage.FEETYPE_DUPLICATE_ERROR,
      });
      return;
    }

    // 3. Create new fee type
    const payload = {
      feeType: trimmedName,
      availableFlag: formData.availableFlag ? 1 : 0,
    };
    try {
      const response = await Axios.post(ApiEndpoints.UPSERT_PACKAGE_FEETYPE, [
        payload,
      ]);
      const newFeeTypes = [...allFeeTypes, ...response.data.data];
      setAllFeeTypes(newFeeTypes);
      setFormData({ feeType: '', availableFlag: true });
      setIsAdding(false);
      showToastMessage('Fee type added successfully.', 'success');
      // Optionally select the newly added fee type
      const newAdded = response.data.data[0];
      if (newAdded) {
        const newSelection: ServiceRateFeetypeSelection = {
          id: newAdded.id,
          feeType: newAdded.feeType,
          unit: newAdded.unit,
        };
        setSelectedRow(newSelection);
        onSelect(newSelection);
        onClose();
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
    setErrors({});
  };

  const handleDelete = async (
    id: number | undefined,
    index: number,
  ): Promise<void> => {
    // Prevent deletion if the fee type is in use
    if (id !== undefined && usedFeeTypeIds.includes(id)) {
      showToastMessage(
        'This fee type is currently used by one or more packaging types and cannot be deleted.',
        'error'
      );
      return;
    }

    try {
      if (id !== undefined) {
        await Axios.delete(ApiEndpoints.DELETE_PACKAGE_FEETYPE, {
          data: { ids: [id] },
        });
      }
      const updatedFeeTypes = allFeeTypes.filter(
        (item: FeeType, i: number) => item.id !== id && i !== index,
      );
      setAllFeeTypes(updatedFeeTypes);
      // filtered will update via effect
      showToastMessage('Fee type deleted successfully.', 'success');
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editFormData.feeType.trim()) {
      setErrors({
        feeType: errorMessage.FIELD_REQUIRED,
      });
      return;
    }
    if (editingIndex === null) return;
    setErrors({});
    const updatedFeeType = [...allFeeTypes];
    updatedFeeType[editingIndex] = {
      ...updatedFeeType[editingIndex],
      ...editFormData,
    };
    setAllFeeTypes(updatedFeeType);
    setEditingIndex(null);
    const payload = updatedFeeType.map((feeType) => ({
      ...(feeType.id && { id: feeType.id }),
      feeType: feeType.feeType,
      availableFlag: feeType.availableFlag ? 1 : 0,
    }));
    try {
      const response = await Axios.post(
        ApiEndpoints.UPSERT_PACKAGE_FEETYPE,
        payload
      );
      setAllFeeTypes(response.data.data);
      // filtered will update via effect
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
    setErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddOrCancel = (key?: string): void => {
    setFormData({ feeType: '', availableFlag: true });
    switch (key) {
      case 'add':
        setIsAdding(true);
        break;
      case 'cancel':
        setErrors({});
        setIsAdding(false);
        break;
    }
  };

  return (
    <>
      {!isLoading && (
        <div className="fixed z-10 inset-0 bg-black bg-opacity-40 flex justify-center items-end w-full h-full">
          <div className="bg-white rounded-t-2xl w-full p-6 shadow-lg transition-transform transform translate-y-0 max-h-[70vh] flex flex-col">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
              <h1 className="font-semibold text-lg">Add Fee Type</h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAddOrCancel('add')}
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-dark-primary rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  + Add More
                </button>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    onChange={(e) => setSearchKey(e.target.value)}
                    value={searchKey}
                    autoFocus
                    className="border border-gray-300 rounded px-3 py-1 h-8 w-80 text-sm focus:outline-primary"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />
                </div>
                <button
                  onClick={onClose}
                  type="button"
                  className="text-gray-600 hover:text-gray-900"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[60vh]">
              <table className="w-full table-fixed border-collapse">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr className="border-b">
                    <th className="w-[30%] px-4 py-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">Title</span>
                        <img
                          src={Assets.SORT_ICON}
                          alt="Sort"
                          className="h-3 w-3"
                        />
                      </div>
                    </th>
                    <th className="w-[25%] px-4 py-3 text-center">
                      <span className="text-sm font-semibold">Units</span>
                    </th>
                    <th className="w-[25%] px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeeTypes.length > 0 ? (
                    filteredFeeTypes.map((feeType: FeeType, index: number) => (
                      <tr
                        key={feeType.id ?? index}
                        className={`cursor-pointer transition ${selectedRow?.id === feeType.id
                          ? 'bg-tertiary border-primary'
                          : 'hover:bg-gray-100'
                          }`}
                        onClick={(e) => {
                          if (
                            editingIndex !== index &&
                            !(e.target instanceof HTMLInputElement)
                          ) {
                            handleRowClick({
                              id: feeType.id ?? 0,
                              feeType: feeType.feeType,
                              unit: feeType.unit,
                            });
                          }
                        }}
                      >
                        <td className="px-4 py-3 align-middle text-left">
                          {editingIndex === index ? (
                            <div>
                              <input
                                type="text"
                                name="feeType"
                                value={editFormData.feeType}
                                onChange={handleEditChange}
                                className="w-full border-b-2 border-gray-300 focus:border-primary-500 outline-none bg-transparent"
                              />
                              {errors.feeType && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.feeType}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm font-medium">
                              {feeType.feeType}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span className="text-sm font-medium">
                            {feeType.unit}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <div className="flex justify-end gap-3">
                            {editingIndex === index ? (
                              <>
                                <button
                                  type="button"
                                  onClick={handleSaveEdit}
                                  className="text-primary p-1"
                                >
                                  <FontAwesomeIcon icon={faCheck} className="text-xl" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingIndex(null)}
                                  className="p-1"
                                >
                                  <FontAwesomeIcon
                                    icon={faXmark}
                                    className="text-gray-500 text-xl"
                                  />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(index);
                                  }}
                                  className="p-1"
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(feeType.id, index);
                                  }}
                                  className="p-1"
                                >
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-center p-4">
                        No data available
                      </td>
                    </tr>
                  )}
                  {/* Add new row */}
                  {isAdding && (
                    <tr className="border-b bg-primary-50">
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          name="feeType"
                          placeholder="New Feetype"
                          value={formData.feeType}
                          autoFocus
                          onChange={handleInputChange}
                          className="w-full border-b-2 border-gray-300 focus:border-primary-500 outline-none bg-transparent"
                        />
                        {errors.feeType && (
                          <p className="text-red-500 text-start text-xs mt-1">
                            {errors.feeType}
                          </p>
                        )}
                        {errors.isDuplicate && (
                          <p className="text-red-500 text-start text-xs mt-1">
                            {errors.isDuplicate}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-medium">
                          {WeightType.LBS}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={handleAddFeeType}
                            className="text-primary p-1"
                          >
                            <FontAwesomeIcon icon={faCheck} className="text-xl" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAddOrCancel('cancel')}
                            className="text-gray-600 hover:text-gray-800 p-1"
                          >
                            <FontAwesomeIcon
                              icon={faXmark}
                              className="text-gray-500 text-xl"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};